import { Box,ListItemIcon,ListSubheader } from '@mui/material'
import React,{ useRef,useState } from 'react'
import { useDispatch } from 'react-redux'
import { Dispatch } from '../store'
import { Attribute } from './Attributes'
import { GridListHeaderTitle } from './GridListHeaderTitle'
import { LinearProgress } from './LinearProgress'

const MIN_WIDTH = 50

type Props<TOptions = IDataOptions> = {
  required?: Attribute<TOptions>
  attributes?: Attribute<TOptions>[]
  columnWidths: ILookup<number>
  icon?: React.ReactNode | boolean
  fetching?: boolean
  mobile?: boolean
}

export const GridListHeader = <TOptions,>({
  required,
  attributes = [],
  columnWidths,
  icon,
  fetching,
  mobile,
}: Props<TOptions>) => {
  const { ui } = useDispatch<Dispatch>()
  const [resize, setResize] = useState<number>(0)
  const containerRef = useRef<HTMLLIElement>(null)
  const moveRef = useRef<[string, number, number, number, number]>(['', 0, 0, 0, 0])
  const iconNode = icon === true ? null : icon

  const onDown = (event: React.MouseEvent, attribute: Attribute<TOptions>) => {
    const containerX = containerRef.current?.getBoundingClientRect().left || 0
    const containerY = containerRef.current?.parentElement?.getBoundingClientRect().height || 0
    moveRef.current = [attribute.id, attribute.width(columnWidths), event.clientX, containerX, containerY]
    event.preventDefault()
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  const onUp = (event: MouseEvent) => {
    event.preventDefault()
    window.removeEventListener('mousemove', onMove)
    window.removeEventListener('mouseup', onUp)
    const px = Math.max(moveRef.current[1] + (event.clientX - moveRef.current[2]), MIN_WIDTH)
    ui.resizeColumn({ id: moveRef.current[0], width: px })
    setResize(0)
  }

  const onMove = (event: MouseEvent) => {
    if (moveRef.current[1] + (event.clientX - moveRef.current[2]) < MIN_WIDTH) return
    setResize(event.pageX - moveRef.current[3])
  }

  return (
    <ListSubheader
      sx={theme => ({
        padding: 0,
        minWidth: '100%',
        boxShadow: `inset 0 -1px ${theme.palette.grayLighter.main}`,
      })}
      ref={containerRef}
      disableGutters
    >
      <Box
        component="span"
        sx={theme => ({
          position: 'absolute',
          zIndex: 80,
          top: 0,
          width: '1px',
          borderRight: `1px dotted ${theme.palette.primaryLight.main}`,
        })}
        style={{ left: resize, height: moveRef.current[4], display: resize ? 'block' : 'none' }}
      />
      {required && (
        <GridListHeaderTitle attribute={required} onMouseDown={onDown} sticky>
          {!!icon && <ListItemIcon>{iconNode}</ListItemIcon>}
        </GridListHeaderTitle>
      )}
      {!mobile &&
        attributes?.map(attribute => (
          <GridListHeaderTitle key={attribute.id} attribute={attribute} onMouseDown={onDown} />
        ))}
      <LinearProgress loading={fetching} />
    </ListSubheader>
  )
}
